import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { useEffect, useRef, useState } from "react";
import AsyncSearchSelect from "../common/async_search_select/AsyncSearchSelect";
import { FilterClearButton } from "../common/responsive/filter_sheet";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { Project } from "../../../erp/inventory/models/project_model";
import { CostCenter } from "../../../erp/inventory/models/cost_center_model";
import { ItemModelV2 } from "../../../erp/inventory/models/item_model";
import { PartyModel } from "../../../erp/accounts/models/party_model";
import { WarehouseModel } from "../../../erp/inventory/models/warehouse_model";

const BASE = process.env.REACT_APP_BACKEND_BASE_URL;

interface MultiFilterProps {
  values?: string[];
  onChange: (values: string[] | undefined) => void;
  placeholder?: string;
}

interface AsyncMultiSelectFilterProps<T> extends MultiFilterProps {
  fetcher: (query: string) => Promise<T[]>;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  minChars?: number;
  chipLabel?: (key: string) => string | undefined;
}

/** Async search + removable chips; emits the selected keys (ids/codes). */
function AsyncMultiSelectFilter<T>({
  values,
  onChange,
  placeholder,
  fetcher,
  getKey,
  getLabel,
  minChars,
  chipLabel,
}: AsyncMultiSelectFilterProps<T>) {
  // Labels are only known for picks made in this session; after a reload the
  // chip falls back to the raw key (persisted filter holds keys only).
  const labels = useRef<Record<string, string>>({});
  const selected = values ?? [];

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center gap-1">
        <AsyncSearchSelect<T>
          value={null}
          clearOnSelect
          placeholder={placeholder}
          minChars={minChars}
          fetcher={fetcher}
          getOptionKey={getKey}
          getOptionLabel={getLabel}
          onSelect={(item) => {
            const key = getKey(item);
            if (!key || selected.includes(key)) return;
            labels.current[key] = getLabel(item);
            onChange([...selected, key]);
          }}
        />
        {selected.length > 0 && (
          <FilterClearButton onClick={() => onChange(undefined)} />
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((key) => (
            <span
              key={key}
              className="bg-gray-100 border text-xs py-0.5 pl-2 pr-1 rounded flex items-center gap-1"
            >
              {labels.current[key] ?? chipLabel?.(key) ?? key}
              <button
                type="button"
                onClick={() => {
                  const next = selected.filter((k) => k !== key);
                  onChange(next.length ? next : undefined);
                }}
                className="text-gray-400 hover:text-red-500"
              >
                <Icon path={mdiClose} size={0.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const fetchCostCenters = async (query: string): Promise<CostCenter[]> => {
  const res = await fetchGet(
    `${BASE}/api/v3/erp/inventory/cost_center` +
      toQueryString({ search: query, page: 1, count: 20, all: true }),
  );
  return res.success && Array.isArray(res.data?.data) ? res.data.data : [];
};

const fetchProjects = async (query: string): Promise<Project[]> => {
  const res = await fetchGet(
    `${BASE}/api/v3/erp/inventory/project/` +
      toQueryString({ search: query, page: 1, count: 20, all: true }),
  );
  return res.success && Array.isArray(res.data?.data) ? res.data.data : [];
};

const fetchItems = async (query: string): Promise<ItemModelV2[]> => {
  const res = await fetchGet(
    `${BASE}/api/v3/erp/inventory/items` +
      toQueryString({
        item_name: query,
        item_code: query,
        mpn: query,
        page: 1,
        count: 20,
        all: true,
      }),
  );
  return res.success ? (res.data?.data ?? []) : [];
};

/** Cost center multi-select by id (searches CC id + name). */
export const CostCenterMultiFilter = (props: MultiFilterProps) => (
  <AsyncMultiSelectFilter<CostCenter>
    {...props}
    placeholder={props.placeholder ?? "Search cost center ID / name"}
    fetcher={fetchCostCenters}
    getKey={(c) => c?.id ?? ""}
    getLabel={(c) => `${c.id} · ${c.name}`}
  />
);

/** Project multi-select by id (searches project id + name), open and closed. */
export const ProjectMultiFilter = (props: MultiFilterProps) => (
  <AsyncMultiSelectFilter<Project>
    {...props}
    placeholder={props.placeholder ?? "Search project ID / name"}
    fetcher={fetchProjects}
    getKey={(p) => p?.id ?? ""}
    getLabel={(p) => `${p.id} · ${p.name}`}
  />
);

/** Item multi-select by item code (searches code / name / MPN). */
export const ItemMultiFilter = (props: MultiFilterProps) => (
  <AsyncMultiSelectFilter<ItemModelV2>
    {...props}
    placeholder={props.placeholder ?? "Search item code / name"}
    fetcher={fetchItems}
    getKey={(i) => i?.item_code ?? ""}
    getLabel={(i) => `${i.item_code} · ${i.name}`}
  />
);

const fetchVendors = async (query: string): Promise<PartyModel[]> => {
  const isLikelyId = /^VC-\d+$/i.test(query.trim());
  const res = await fetchGet(
    `${BASE}/api/v3/erp/accounts/party` +
      toQueryString({
        [isLikelyId ? "id" : "name"]: query,
        page: 1,
        count: 20,
        all: true,
      }),
  );
  return res.success && Array.isArray(res.data?.result) ? res.data.result : [];
};

const CREATOR_SOURCES = {
  purchase_order: "/api/v3/erp/purchase-order/creators",
  payment_request:
    "/api/v3/erp/purchase-order/advance_payment_request/creators",
};
type CreatorSource = keyof typeof CREATOR_SOURCES;

const fetchCreators = async (
  source: CreatorSource,
  query: string,
): Promise<{ uid: string; name: string }[]> => {
  const res = await fetchGet(
    `${BASE}${CREATOR_SOURCES[source]}` + toQueryString({ search: query }),
  );
  return res.success && Array.isArray(res.data) ? res.data : [];
};

const fetchWarehouses = async (query: string): Promise<WarehouseModel[]> => {
  const res = await fetchGet(
    `${BASE}/api/v3/erp/inventory/warehouse_type` +
      toQueryString({ search: query, page: 1, count: 20, all: true }),
  );
  return res.success && Array.isArray(res.data?.data) ? res.data.data : [];
};

/** Warehouse multi-select by id (searches warehouse name / id). */
export const WarehouseMultiFilter = (props: MultiFilterProps) => (
  <AsyncMultiSelectFilter<WarehouseModel>
    {...props}
    placeholder={props.placeholder ?? "Search warehouse name / ID"}
    fetcher={fetchWarehouses}
    getKey={(w) => w?.id ?? ""}
    getLabel={(w) => `${w.id} · ${w.name}`}
  />
);

/** Vendor (party) multi-select by id (searches vendor name or VC- id). */
export const VendorMultiFilter = (props: MultiFilterProps) => (
  <AsyncMultiSelectFilter<PartyModel>
    {...props}
    placeholder={props.placeholder ?? "Search vendor name / ID"}
    fetcher={fetchVendors}
    getKey={(v) => v?.id ?? ""}
    getLabel={(v) => `${v.id} · ${v.name}`}
  />
);

/** "Created By" multi-select listing users who created records of `source`.
 * A key may be a comma-joined uid list (same-name accounts merged by the
 * backend). */
export const CreatorMultiFilter = ({
  source,
  ...props
}: MultiFilterProps & { source: CreatorSource }) => {
  const [names, setNames] = useState<Record<string, string>>({});
  const hasValues = (props.values?.length ?? 0) > 0;
  useEffect(() => {
    if (!hasValues) return;
    fetchCreators(source, "").then((list) =>
      setNames(
        Object.fromEntries(
          list.flatMap((u) => [
            [u.uid, u.name],
            ...u.uid.split(",").map((id) => [id, u.name]),
          ]),
        ),
      ),
    );
  }, [hasValues, source]);
  return (
    <AsyncMultiSelectFilter<{ uid: string; name: string }>
      {...props}
      placeholder={props.placeholder ?? "Search user name"}
      minChars={0}
      fetcher={async (query) =>
        (await fetchCreators(source, query)).filter(
          (u) => !(props.values ?? []).includes(u.uid),
        )
      }
      getKey={(u) => u?.uid ?? ""}
      getLabel={(u) => u.name}
      chipLabel={(key) => names[key]}
    />
  );
};

export const PoCreatorMultiFilter = (props: MultiFilterProps) => (
  <CreatorMultiFilter {...props} source="purchase_order" />
);

/** Fixed option list (statuses) with the same look as the async filters. */
export const OptionsMultiFilter = ({
  options,
  ...props
}: MultiFilterProps & { options: string[] }) => (
  <AsyncMultiSelectFilter<string>
    {...props}
    minChars={0}
    fetcher={async (query) =>
      options.filter(
        (o) =>
          !(props.values ?? []).includes(o) &&
          o.toLowerCase().includes(query.trim().toLowerCase()),
      )
    }
    getKey={(o) => o}
    getLabel={(o) => o}
    chipLabel={(key) => key}
  />
);
